import { useState } from "react";
import { toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import TuneIcon from "@mui/icons-material/Tune";
import { Tooltip, TextField, MenuItem } from "@mui/material";
import getColumns from "./column.js";
import DynamicTable from "../../../components/common/DynamicTable.jsx";
import useModal from "../../../hooks/useModal.js";
import useAuthUser from "../../../hooks/useAuthUser.js";
import { getPayrollRuns, getPayrollRunDetails, downloadPayslip, PAYROLL_STATUS_LABELS } from "../../../services/payrollPeriodic.js";
import GeneratePayroll from "./GeneratePayroll.jsx";
import BulkGeneratePayroll from "./BulkGeneratePayroll.jsx";
import PayrollDetail from "./PayrollDetail.jsx";
import usePaginatedList from "../../../hooks/usePaginatedList.js";

const PAY_BASIS_OPTIONS = [
  { label: "Daily", value: "DAILY" },
  { label: "Weekly", value: "WEEKLY" },
  { label: "Bi-weekly", value: "BIWEEKLY" },
  { label: "Hourly", value: "HOURLY" },
];

const PayrollRunsList = () => {
  const { isAdminHrRole } = useAuthUser();
  const { showModal } = useModal();
  const [showFilter, setShowFilter] = useState(false);
  const [filterValues, setFilterValues] = useState({});
  const [downloadingId, setDownloadingId] = useState(null);

  const fetchData = async (params) => {
    return await getPayrollRuns(params.page, params);
  };

  const { data, pagination, setPage, setItemsPerPage, setFilters, refresh } = usePaginatedList(fetchData);

  const handleApplyFilters = () => {
    setFilters({ filters: filterValues });
    setShowFilter(false);
  };

  const handleClearFilters = () => {
    setFilterValues({});
    setFilters({ filters: {} });
    setShowFilter(false);
  };

  const handleGenerate = () => {
    showModal("Generate Payroll", <GeneratePayroll refresh={refresh} />, "lg");
  };

  const handleBulkGenerate = () => {
    showModal("Bulk Generate Payroll", <BulkGeneratePayroll refresh={refresh} />, "lg");
  };

  const handleView = async (row) => {
    try {
      const response = await getPayrollRunDetails(row.id);
      showModal("Payroll Details", <PayrollDetail refresh={refresh} record={response?.data?.data} />, "lg");
    } catch (error) {
      console.error("Error fetching payroll details:", error);
    }
  };

  const handleDownload = async (row) => {
    if (downloadingId === row.id) return;
    setDownloadingId(row.id);
    try {
      const response = await downloadPayslip(row.id);
      const disposition = response.headers?.["content-disposition"] || "";
      const fromHeader = /filename="?([^";]+)"?/i.exec(disposition)?.[1];
      const filename = fromHeader || `SalarySlip_${row.employee_code || row.employee_id}_${String(row.period_start).slice(0, 10)}.pdf`;
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Payslip downloaded successfully!");
    } catch (error) {
      // Error bodies arrive as a Blob because of responseType: "blob".
      let message = "Failed to download payslip. Please try again.";
      try {
        const body = error?.response?.data;
        if (body instanceof Blob) message = JSON.parse(await body.text())?.message || message;
      } catch (_) {
        /* keep default message */
      }
      toast.error(message);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <>
      <div className="bg-light mb-2">
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <label className="mb-0 text-muted small">Show</label>
              <select
                className="form-select form-select-sm"
                style={{ width: "80px" }}
                value={pagination?.itemsPerPage || 20}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-muted small">entries</span>
            </div>

            <div className="d-flex gap-2 flex-wrap">
              {isAdminHrRole && (
                <>
                  <button className="btn btn-success btn-sm d-flex align-items-center" onClick={handleGenerate}>
                    <AddIcon sx={{ fontSize: 18 }} className="me-1" />
                    <span className="d-none d-sm-inline">Generate Payroll</span>
                  </button>
                  <button className="btn btn-outline-success btn-sm d-flex align-items-center" onClick={handleBulkGenerate}>
                    <PlaylistAddCheckIcon sx={{ fontSize: 18 }} className="me-1" />
                    <span className="d-none d-sm-inline">Bulk Generate</span>
                  </button>
                </>
              )}

              <Tooltip title="Filter">
                <button className="btn btn-outline-dark btn-sm" onClick={() => setShowFilter(!showFilter)}>
                  <TuneIcon sx={{ fontSize: 18 }} />
                </button>
              </Tooltip>
            </div>
          </div>

          {showFilter && (
            <div className="d-flex flex-wrap gap-2 mt-3">
              <TextField
                select
                size="small"
                label="Pay Basis"
                style={{ minWidth: 160 }}
                value={filterValues.pay_basis || ""}
                onChange={(e) => setFilterValues((prev) => ({ ...prev, pay_basis: e.target.value }))}
              >
                <MenuItem value="">All</MenuItem>
                {PAY_BASIS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label="Status"
                style={{ minWidth: 160 }}
                value={filterValues.status ?? ""}
                onChange={(e) => setFilterValues((prev) => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="">All</MenuItem>
                {Object.entries(PAYROLL_STATUS_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>

              <button className="btn btn-danger btn-sm" onClick={handleApplyFilters}>
                Apply
              </button>
              <button className="btn btn-secondary btn-sm" onClick={handleClearFilters}>
                Clear
              </button>
            </div>
          )}
        </div>
      </div>

      <DynamicTable
        columns={getColumns(handleView, handleDownload, downloadingId)}
        data={data}
        rowKey="id"
        currentPage={pagination?.currentPage}
        totalPages={pagination?.totalPages}
        itemsPerPage={pagination?.itemsPerPage}
        totalItems={pagination?.totalItems}
        onPageChange={(page) => setPage(page)}
      />
    </>
  );
};

export default PayrollRunsList;
